import { Injectable, inject } from '@angular/core';
import {
  AuthService,
  AuthServiceInjectionToken,
} from '@fantaz/shared/data-access/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import * as EnableMfaActions from './mfa-preference.actions';
import { ToastService } from '@fantaz/shared/utils/toast';
import { Store } from '@ngrx/store';
import { selectMfAPreference } from './mfa-preference.selectors';
import { UpdateMFAPreferenceInput } from 'aws-amplify/auth';
import { CoreRouterActions } from '@fantaz/core';
import { ProfileActions } from '@fantaz/profile/data-access';
import { selectAuthUserDevice } from '@fantaz/shared/auth';

@Injectable()
export class MfaPreferenceEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private authService: AuthService = inject(AuthServiceInjectionToken);
  private toastService = inject(ToastService);

  initEnableMfa$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnableMfaActions.updateMFAPreferenceMfa),
      withLatestFrom(this.store.select(selectMfAPreference)),
      switchMap(([{ updateMFAPreference: enableMfaMethod }, mfAPreference]) => {
        let args: UpdateMFAPreferenceInput = {};
        if (
          mfAPreference &&
          mfAPreference.enabled &&
          mfAPreference.enabled.includes('SMS') &&
          !enableMfaMethod.sms
        ) {
          args.sms = 'ENABLED';
        }
        if (
          mfAPreference &&
          mfAPreference.enabled &&
          mfAPreference.enabled.includes('TOTP') &&
          !enableMfaMethod.totp
        ) {
          args.totp = 'ENABLED';
        }
        args = { ...args, ...enableMfaMethod };

        return this.authService.updateMFAPreference(args).pipe(
          mergeMap((data) => {
            return [
              EnableMfaActions.updateEnableMfaSuccess({
                updateMFAPreference: enableMfaMethod,
              }),
              EnableMfaActions.AuthMFAActions.sendMfANotification(
                enableMfaMethod,
              ),
              EnableMfaActions.AuthMFAActions.loadMfAPreference(),
            ];
          }),
          catchError((error: any) => {
            return [EnableMfaActions.UpdateEnableMfaFailure({ error })];
          }),
        );
      }),
      catchError((error) => {
        console.error('Error', error);
        return of(EnableMfaActions.UpdateEnableMfaFailure({ error }));
      }),
    ),
  );

  sendMfANotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnableMfaActions.AuthMFAActions.sendMfANotification),
      withLatestFrom(this.store.select(selectAuthUserDevice)),
      switchMap(([{ payload }, deviceKey]) => {
        return this.authService.mfaNotification(deviceKey || '', payload).pipe(
          switchMap((data) => {
            return [
              EnableMfaActions.AuthMFAActions.sendMfANotificationSuccess(),
            ];
          }),
          catchError((error: any) => {
            return [
              EnableMfaActions.AuthMFAActions.sendMfANotificationFailure({
                error,
              }),
            ];
          }),
        );
      }),
      catchError((error) => {
        console.error('Error', error);
        return of(EnableMfaActions.UpdateEnableMfaFailure({ error }));
      }),
    ),
  );

  // This effect listens for the 'loadMfAPreference' action.
  // When that action is dispatched, it calls the 'fetchMFAPreference' method from the authService.
  // If the fetch is successful, it dispatches the 'loadMfAPreferenceSuccess' action with the fetched data.
  // If an error occurs during the fetch, it dispatches the 'loadMfAPreferenceFailure' action with the error.
  toggleSmsAuthentication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnableMfaActions.AuthMFAActions.loadMfAPreference),
      switchMap(() =>
        this.authService.fetchMFAPreference().pipe(
          map((data) =>
            EnableMfaActions.AuthMFAActions.loadMfAPreferenceSuccess(data),
          ),
          catchError((error) => [
            EnableMfaActions.AuthMFAActions.loadMfAPreferenceFailure({ error }),
          ]),
        ),
      ),
    ),
  );

  loadEnableMfaSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnableMfaActions.updateEnableMfaSuccess),
      switchMap(({ updateMFAPreference }) => {
        if (updateMFAPreference.totp === 'DISABLED') {
          this.toastService.openSnackBar(
            'Authenticator app has been disabled.',
          );
          return [
            CoreRouterActions.navigate(['', { outlets: { dialog: null } }], {
              replaceUrl: true,
            }),
          ];
        } else if (updateMFAPreference.sms === 'DISABLED') {
          this.toastService.openSnackBar(
            'SMS Authentication has been disabled.',
          );
        }
        return [
          ProfileActions.update({
            payload: {
              mfaPreference: updateMFAPreference as {
                sms: string;
                totp: string;
              },
            },
          }),
        ];
      }),
    ),
  );

  changeTotpMfaPreference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EnableMfaActions.changeTotpMfaPreference),
      switchMap(() => {
        return [
          CoreRouterActions.navigate(
            ['', { outlets: { dialog: 'authenticator-mfa' } }],
            { replaceUrl: true },
          ),
        ];
      }),
    ),
  );
}
